import React from 'react';
import Layout from '../components/layout/Layout';
import { BRAND } from '../data/mock';
import { RefreshCw } from 'lucide-react';

const RefundPolicyPage = () => {
  return (
    <Layout>
      {/* Header */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--cream))] via-white to-[hsl(var(--peach))]">
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-[hsl(var(--mint))]/20 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[hsl(var(--coral-light))] mb-6">
            <RefreshCw className="w-8 h-8 text-[hsl(var(--coral))]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Refund Policy</h1>
          <p className="text-lg text-gray-600">Last updated: June 2025</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Quick Summary Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { emoji: "✅", title: "Eligible for Refund", desc: "Damaged, defective, or wrong item received" },
              { emoji: "🔄", title: "Exchange Available", desc: "Size issues within 7 days of delivery" },
              { emoji: "❌", title: "Not Eligible", desc: "Change of mind after artwork approval" },
            ].map((card, i) => (
              <div key={i} className="bg-[hsl(var(--cream))] rounded-2xl p-6 text-center border border-gray-100">
                <div className="text-3xl mb-3">{card.emoji}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{card.title}</h3>
                <p className="text-sm text-gray-600">{card.desc}</p>
              </div>
            ))}
          </div>

          <div className="prose prose-lg max-w-none">

            <div className="bg-[hsl(var(--cream))] rounded-2xl p-6 mb-10 border border-[hsl(350,70%,65%)]/20">
              <p className="text-gray-700 leading-relaxed m-0">
                At <strong>{BRAND.name}</strong>, every product is custom-made with care specifically for you. 
                Because of the personalised nature of our products, our refund policy is designed to be 
                fair to both our customers and our team. Please read this policy carefully before placing your order.
              </p>
            </div>

            {[
              {
                title: "1. Eligibility for Refund or Replacement",
                color: "hsl(350,70%,65%)",
                content: [
                  "You received a damaged or defective product — the print is faded, peeling, or has a manufacturing defect.",
                  "You received the wrong item — incorrect size, colour, or design compared to what was approved.",
                  "The product was lost in transit and confirmed undelivered by the courier.",
                  "The print quality significantly differs from the approved artwork preview.",
                  "Claims must be raised within 7 days of delivery with photo or video evidence."
                ]
              },
              {
                title: "2. Non-Refundable Situations",
                color: "hsl(20,90%,70%)",
                content: [
                  "Change of mind after artwork has been approved and production has started.",
                  "Incorrect size ordered — please refer to our Size Guide before ordering.",
                  "Slight colour variation between the screen preview and the final print, as this is normal in printing.",
                  "Damage caused by improper washing or handling after delivery.",
                  "Orders where the customer provided incorrect shipping information.",
                  "Custom orders that have already been shipped and are not damaged or defective."
                ]
              },
              {
                title: "3. How to Raise a Refund or Replacement Request",
                color: "hsl(350,70%,65%)",
                content: [
                  "Step 1: Contact us within 7 days of delivery via email at " + BRAND.email + " or via Instagram DM @aura_printiqo.",
                  "Step 2: Share your order details including order ID, name, and contact number.",
                  "Step 3: Attach clear photos or a video showing the defect or issue with the product.",
                  "Step 4: Our team will review your request within 2-3 business days and respond with next steps.",
                  "Step 5: If approved, we will arrange a replacement or initiate a refund as applicable."
                ]
              },
              {
                title: "4. Refund Process",
                color: "hsl(20,90%,70%)",
                content: [
                  "Approved refunds will be processed within 5-7 business days of claim approval.",
                  "Refunds are issued to the original payment method used at the time of purchase.",
                  "UPI and bank transfer refunds typically reflect within 2-3 business days after processing.",
                  "Card refunds may take 5-10 business days depending on your bank.",
                  "For COD orders, refunds will be processed via bank transfer. You will need to provide your bank details."
                ]
              },
              {
                title: "5. Replacement Process",
                color: "hsl(350,70%,65%)",
                content: [
                  "If a replacement is approved, we will begin production of the new item at no additional cost.",
                  "The replacement will be shipped within the standard production timeline of 5-10 business days.",
                  "In some cases, we may request you to return the defective item before dispatching the replacement.",
                  "Return shipping costs for defective items will be borne by us.",
                  "Only one replacement per order is permitted."
                ]
              },
              {
                title: "6. Size Exchange Policy",
                color: "hsl(20,90%,70%)",
                content: [
                  "We offer size exchanges if the product is unused, unwashed, and in original condition.",
                  "Size exchange requests must be raised within 7 days of delivery.",
                  "The customer is responsible for return shipping costs for size exchanges.",
                  "Exchanges are subject to size availability. If the requested size is unavailable, a refund will be issued.",
                  "Please refer to our detailed Size Guide before ordering to avoid size-related issues."
                ]
              },
              {
                title: "7. Cancellation Policy",
                color: "hsl(350,70%,65%)",
                content: [
                  "Orders can be cancelled only before the artwork preview is shared with you.",
                  "Once artwork has been shared for approval, cancellations are not accepted.",
                  "To request cancellation, contact us immediately at " + BRAND.email + " with your order details.",
                  "If production has not yet started, a full refund will be issued within 5-7 business days.",
                  "Bulk corporate orders require 48 hours advance notice for cancellation before production begins."
                ]
              },
              {
                title: "8. Contact Us",
                color: "hsl(20,90%,70%)",
                content: [
                  "We are committed to resolving all issues quickly and fairly. Please reach out to us:",
                  "Email: " + BRAND.email,
                  "Instagram: @aura_printiqo",
                  "Response time: Within 48 hours on business days (Monday to Saturday, 10 AM to 6 PM IST)."
                ]
              }
            ].map((section, index) => (
              <div key={index} className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                  {section.title}
                </h2>
                <ul className="space-y-3">
                  {section.content.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: section.color }} />
                      <span className="text-gray-600 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default RefundPolicyPage;
